import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import styles from './HurdleDiscount.module.scss';

import { HurdleDiscountParams } from 'chaika-frontend-types/frontend-cms';

import { translate } from '@chaika/i18n/helper';
import { Input } from '@components/Input';
import { Button, ButtonModifiers, ButtonTheme } from '@components/Button';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Props {
    params?: HurdleDiscountParams[];
    onChange(params: HurdleDiscountParams[]): void;
}

const defaultParams: HurdleDiscountParams = {
    rate: 0,
    hurdle: 0,
};

export const HurdleDiscount = observer(({ params, onChange }: Props) => {
    const [hurdles, setHurdles] = useState(params ?? [defaultParams]);

    const onChangeItem = useCallback((event: React.ChangeEvent<HTMLInputElement>, index: number, name?: string) => {
        const { value } = event.target;
        assertIsDefined(name, 'Hurdle discount inputs are rendered with a name');

        setHurdles(prev => prev.map((item, i) => (i === index ? { ...item, [name]: value } : item)));
    }, []);

    const onDeleteClick = useCallback((index: number) => {
        setHurdles(prev => prev.filter((_, i) => i !== index));
    }, []);

    const onAddClick = useCallback(() => {
        setHurdles([...hurdles, defaultParams]);
    }, [hurdles]);

    useEffect(() => {
        onChange(hurdles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hurdles]);

    const hasDefaultParams = !!hurdles.find(it => it.rate === defaultParams.rate && it.hurdle === defaultParams.hurdle);

    return (
        <div className={styles.root}>
            <div className={styles.list}>
                {hurdles.map((it, index) => (
                    <div
                        className={styles.item}
                        key={index}
                    >
                        <div className={styles.inputs}>
                            <Input
                                title={translate('hurdle')}
                                type={'number'}
                                name={'hurdle'}
                                min={1}
                                value={it.hurdle}
                                dataTest={''}
                                onChange={(params, name) => onChangeItem(params, index, name)}
                            />
                            <Input
                                title={`${translate('rate')} ${translate('from').toLowerCase()} 0 ${translate(
                                    'toSecond',
                                ).toLowerCase()} 1`}
                                type={'number'}
                                name={'rate'}
                                min={0}
                                max={1}
                                value={it.rate}
                                dataTest={''}
                                onChange={(params, name) => onChangeItem(params, index, name)}
                            />
                        </div>
                        <div className={styles.button}>
                            <Button
                                modifiers={[ButtonModifiers.Small]}
                                theme={ButtonTheme.Secondary}
                                disabled={hurdles.length <= 1}
                                onClick={() => onDeleteClick(index)}
                                label={translate('delete')}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <div>
                <Button
                    modifiers={[ButtonModifiers.Small]}
                    theme={ButtonTheme.Primary}
                    disabled={hasDefaultParams}
                    onClick={onAddClick}
                    label={translate('add')}
                />
            </div>
        </div>
    );
});
